// StageCelebration - Celebration modal shown when unlocking a new stage/zone

import { useEffect } from 'react';
import { useTTS } from '../../hooks/useTTS';

const StageCelebration = ({ data, onClose, voiceEnabled = true, playerVoice = 'alice' }) => {
  const { speakAsPlayer, stop: stopTTS } = useTTS();
  
  const zoneInfo = {
    2: { name: 'Northern Forest', color: '#22C55E', desc: 'Explore the dense pine forests!' },
    3: { name: 'Eastern Hills', color: '#6BCB77', desc: 'Climb the rolling green hills!' },
    4: { name: 'Southern Beach', color: '#FFD93D', desc: 'Relax on the sunny beaches!' },
    5: { name: 'Western Mountains', color: '#A66CFF', desc: 'Conquer the mighty mountains!' },
  };
  
  const zone = data ? (zoneInfo[data.new_stage] || zoneInfo[2]) : zoneInfo[2];
  
  useEffect(() => {
    if (data && voiceEnabled) {
      const msg = `Amazing! Stage ${data.new_stage} unlocked! Welcome to ${zone.name}! ${zone.desc}`;
      setTimeout(() => speakAsPlayer(msg, playerVoice), 500);
    }
    return () => stopTTS();
  }, [data]);
  
  if (!data) return null;
  
  const { new_stage, stage_name } = data;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="stage-celebration-modal">
      <div 
        className="bg-gradient-to-br from-white via-cream to-white rounded-3xl p-8 max-w-lg w-full text-center shadow-2xl border-4 animate-stage-unlock"
        style={{ borderColor: zone.color }}
      >
        {/* Sparkle effects */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-sparkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            >
              *
            </div>
          ))}
        </div>
        
        <h2 className="font-display text-4xl text-text-dark mb-2">
          STAGE {new_stage} UNLOCKED!
        </h2>
        
        <p className="font-display text-2xl mb-4" style={{ color: zone.color }}>
          {stage_name}
        </p>
        
        <div 
          className="rounded-2xl p-6 mb-6 border-2"
          style={{ backgroundColor: `${zone.color}20`, borderColor: `${zone.color}50` }}
        >
          <p className="font-display text-2xl mb-2" style={{ color: zone.color }}>
            {zone.name}
          </p>
          <p className="text-text-medium">{zone.desc}</p>
        </div>
        
        <p className="text-text-medium mb-6">
          New area now accessible! Explore and find new NPCs!
        </p>
        
        <button
          onClick={() => { stopTTS(); onClose(); }}
          className="w-full py-4 rounded-2xl text-white font-display text-xl transition-all shadow-lg"
          style={{ background: `linear-gradient(135deg, ${zone.color}, #4ECDC4)` }}
          data-testid="stage-celebration-explore"
        >
          Explore {zone.name}!
        </button>
      </div>
      
      <style>{`
        @keyframes stage-unlock {
          0% { transform: scale(0) rotate(-10deg); opacity: 0; }
          50% { transform: scale(1.1) rotate(5deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        .animate-stage-unlock {
          animation: stage-unlock 0.8s ease-out;
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        .animate-sparkle {
          animation: sparkle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default StageCelebration;
